import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { REMOVE_COLLECTION, UPDATE_COLLECTION, UPLOAD_COLLECTION_ASSET } from '../graphql';
import {
  CollectionLevel,
  CollectionType,
  ContentCategoriesQuery,
  GetBadgesQuery,
  GetItemsQuery,
  LevelRewardInput,
  type Collection,
} from '../../../../../../__gqltypes__';
import CollectionForm from './CollectionForm';
import DeleteVanityModal from '../../Items/components/DeleteVanityModal';
import { GET_CONTENT_CATEGORIES } from '../../../components/apolloQueries';
import AssetDisplay from '../../Items/components/AssetDisplay';
import DragUpload from '../../Items/components/DragUpload';

type Props = {
  collection: Collection;
  refetch: () => void;
  badgesData?: GetBadgesQuery;
  vanityData?: GetItemsQuery;
};

export default function CollectionView({ badgesData, vanityData, collection, refetch }: Props) {
  const [collectionData, setCollection] = useState(collection);

  useEffect(() => {
    setCollection(collection);
  }, [collection]);
  const [updateCollection, { loading }] = useMutation(UPDATE_COLLECTION, {
    onCompleted: () => refetch(),
  });
  const history = useHistory();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [deleteCollection] = useMutation(REMOVE_COLLECTION, {
    onCompleted: () => refetch(),
  });
  const { data: categoryData } = useQuery<ContentCategoriesQuery>(GET_CONTENT_CATEGORIES, {
    skip: collectionData?.type !== CollectionType.MUSIC,
  });

  const categories = useMemo(() => categoryData?.app?.contentCategories, [categoryData]);

  const [uploadAsset, { loading: assetLoading }] = useMutation(UPLOAD_COLLECTION_ASSET);

  const handleUpload = (file: Blob) => {
    uploadAsset({
      variables: {
        input: {
          file,
          id: collectionData.id,
        },
      },
    }).then(({ data }) => {
      setCollection(data.uploadCollectionAssets);
    });
  };

  const handleUpdate = () => {
    updateCollection({
      variables: {
        input: {
          id: collectionData?.id,
          name: collectionData?.name,
          type: collectionData?.type,
          categoryId: collectionData?.categoryId,
          asset: collectionData?.asset,
          levels: collectionData?.levels.map((l) => ({
            items: l.items?.map((i) => {
              const { __typename, ...rest } = i;
              return rest;
            }),
            rewards: l.rewards?.map((r) => _.omit(r, '__typename')),
          })),
          isDraft: collectionData?.isDraft,
        },
      },
    });
  };

  const handleDelete = () => {
    deleteCollection({ variables: { collectionId: collectionData?.id } });
    history.push('/sp3/liveops/collections/:id');
    setShowDeleteModal(false);
  };

  const handleAddLevel = () => {
    const levels = collectionData?.levels?.length ? [...collectionData.levels] : [];
    levels.push(levels[0] ?? { rewards: [], items: [] });
    setCollection({ ...collectionData, levels });
  };

  const handleDeleteLevel = () => {
    const levels = collectionData?.levels?.length ? [...collectionData.levels] : [];
    levels.pop();
    setCollection({ ...collectionData, levels });
  };

  const iterate = (root: Record<string, string> | string, acc: string[]) => {
    if (_.isString(root)) {
      if (root.startsWith('https')) {
        acc.push(root);
      }
      return;
    }
    if (_.isArray(root)) {
      root.forEach((r) => iterate(r, acc));
      return;
    }

    if (!_.isObject(root)) {
      return;
    }

    const val = Object.values(root);
    val.forEach((v) => iterate(v, acc));
  };

  return (
    <>
      <Card className="mt-3">
        <LoadingLogo show={loading} />
        <DeleteVanityModal
          type="Collection"
          name={collectionData?.name}
          show={showDeleteModal && !loading}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
        <Card.Header className="text-center d-flex justify-content-between">
          <h4>{collectionData?.name}</h4>
          <div>
            <Button className="mr-3" variant="danger" onClick={() => setShowDeleteModal(true)}>
              Delete
            </Button>
            <Button onClick={handleUpdate}>Save</Button>
          </div>
        </Card.Header>
        <Card.Body style={{ height: '100%' }}>
          <Form>
            <Form.Group>
              <Form.Label>Id</Form.Label>
              <Form.Control placeholder={collectionData?.id} disabled />
            </Form.Group>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                placeholder=""
                value={collectionData?.name}
                onChange={(e) => setCollection({ ...collectionData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Is Draft</Form.Label>
              <Form.Check
                type="checkbox"
                checked={collectionData?.isDraft}
                onChange={(e) => setCollection({ ...collectionData, isDraft: e.target.checked })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Control as="select" value={collectionData?.type} disabled>
                {Object.keys(CollectionType).map((type) => (
                  <option value={type} key={type}>
                    {type}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {collectionData?.type === CollectionType.MUSIC ? (
              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Control
                  as="select"
                  value={collectionData?.categoryId ?? ''}
                  onChange={(e) => {
                    setCollection({
                      ...collectionData,
                      categoryId: e.target.value,
                    });
                  }}
                >
                  <option value="">Choose a category</option>
                  {categories &&
                    categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </Form.Control>
              </Form.Group>
            ) : (
              <>
                <Form.Group>
                  <Form.Label>Asset</Form.Label>
                  <Form.Control value={collectionData?.asset ?? ''} disabled />
                </Form.Group>

                <LoadingLogo show={assetLoading} />

                <DragUpload onUpload={handleUpload} />
                {!!collectionData?.asset?.length && <AssetDisplay assetUrl={collectionData?.asset} />}
              </>
            )}
            <hr />
          </Form>
          {collectionData?.levels?.map((_level: CollectionLevel, index: number) => (
            <CollectionForm
              index={index}
              level={_level}
              collectionData={collectionData}
              setLevels={(lvl) => {
                const newLevels = [...collectionData.levels];
                newLevels[index] = lvl;
                setCollection({ ...collectionData, levels: newLevels });
              }}
              vanityData={vanityData}
              badgesData={badgesData}
              key={collectionData.id + index}
            />
          ))}
          <br />
          <div className="d-flex justify-content-between">
            <Button variant="success" onClick={handleAddLevel}>
              <i className="fas fa-plus-circle mr-2" />
              Add Level
            </Button>
            <Button variant="danger" onClick={handleDeleteLevel}>
              <i className="fas fa-minus-circle mr-2" /> Remove level
            </Button>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
